import React, { lazy, memo, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useAppContext } from '@/hooks/AppContext';
import Header from '@/components/Header';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';
import dayJs from 'dayjs';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper';
import { ServerContext } from '@/type/pages';

// import CategoryCard from '@/components/common/category-card';
import SearchInput from '@/components/SearchInput';
import EditorChoice from '@/components/common/editor-choice';
import PromotionBanner from '@/components/common/promotion-banner';
import BlogSwiper from '@/components/common/blog-swiper';
import PromotionCard from '@/components/common/promotion-card';
import ScrollTag from '@/components/ScrollTag';
import GuideArticleBlock from '@/components/GuideArticleBlock';
import CategoryCard from '@/components/common/category-card';

import CategoryWrapper from '@/components/CategoryWrapper';

import { XyzTransition } from '@animxyz/react';
import placeholder from '@/public/img/PlaceholderImage.png';
import { filterSvgPath } from '@/utils/dataFormat';
import { getAllCategory, getAllCategoryLanding } from '@/services/category';
import { getEntryByContentTypeUrl, getMultiplePopupBanner, getPromotionBanner } from '@/services/contentstack';
import { getHeaderAndFooter } from '@/services/headerAndFooter';
import { HEADER_FOOTER_CONTENT_TYPE } from '@/constants/headerAndFooter';
import { livePreview, onEntryChange } from '@/contentstack-sdk';
import {
  ECOM_PROMOTIONS,
  headerAndFooterIncludeReference,
  HOME_PAGE_CONTENT_TYPE,
  HOME_PAGE_URL,
  homePageIncludeReference,
  otherHomePageReference,
  SHARED_BANNER_MODAL,
} from '@/constants/homePage';
import { AppState, wrapper } from '@/store';
import { changeAllCategory, changeAllCategoryLanding } from '@/store/slices/categoryPageSlice';
import {
  changeHomePage,
  changePopupBanner,
  changePromotionBanner,
  getHomePageSuccess,
} from '@/store/slices/homePageSlice';
import { getHomePageAction, getHomePageLivePreviewAction } from '@/store/actions/homePageAction';
import { changeCategoryList, changeHeaderFooter } from '@/store/slices/headerAndFooterSlice';

import styles from '@/styles/pages/blog.module.scss';
import { error, info } from '@/utils/safetyLog';

export default memo((props: any) => {
  const router = useRouter();
  const dispatch = useDispatch<any>();
  const { keyword } = useSelector(
    (state: AppState) => ({
      keyword: state.sortFilter.keyword,
    }),
    shallowEqual,
  );
  const [isMobile, setMobile] = useState(false);
  const { headerFooter } = useAppContext();
  const { language } = useAppContext();
  const { page } = useAppContext();
  const { body } = props;

  const goToSearch = (e: any, tag?: any) => {
    e.stopPropagation();
    let uri = `/${language.origin}/blog/search`;
    if (keyword) uri = `/${language.origin}/blog/search?q=${keyword}`;
    // unknown reason about missing header's css, so use this way to create new tab // 2024.06.20
    // router.push(uri);
    window.location.href = `${window.location.origin}${uri}`;
  };

  useEffect(() => {
    onEntryChange(async () => {
      const res: any = await livePreview;
      const preview = res.config.stackSdk.live_preview;
      if (!preview.live_preview || preview.content_type_uid !== 'blog_landing_page') return;
      await dispatch(getHomePageLivePreviewAction(preview));
    });
  }, [dispatch, props.pathName]);

  useEffect(() => {
    setTimeout(() => {
      window?.dataLayer?.push({
        event: 'content_landing',
        author: '',
        description: '',
        publish_time: page?.publish_details?.time,
        subtitle: '',
        tags: '',
        pageURL: window.location.href,
        content_Group: 'blog home',
      });
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const bodyElement = document.querySelector('.Blog_Body') as HTMLElement;
    const childElements = Array.from(bodyElement.children);
    const filteredChildElements = childElements.filter(
      childElement => !childElement.classList.contains('Follow_Background_Color'),
    );
    filteredChildElements.forEach((childElement, index) => {
      const isOdd = index % 2 === 0;
      if (isOdd) {
        childElement.classList.add('Odd-Item');
      } else {
        childElement.classList.add('Even-Item');
      }
    });

    let prevColor = '';

    for (let i = 0; i < childElements.length; i++) {
      const childElement = childElements[i] as HTMLElement;

      if (childElement.classList.contains('Follow_Background_Color')) {
        childElement.style.backgroundColor = prevColor;
      } else if (childElement.classList.contains('Search_Bar')) {
        prevColor = '#F8F9F9';
      } else if (childElement.classList.contains('Category_Card')) {
        prevColor = '#F8F9F9';
      } else if (childElement.classList.contains('Odd-Item')) {
        childElement.style.backgroundColor = '#F8F9F9';
        prevColor = '#F8F9F9';
      } else if (childElement.classList.contains('Even-Item')) {
        childElement.style.backgroundColor = '#EDEFF0';
        prevColor = '#EDEFF0';
      }
    }
  }, [language.origin]);

  useEffect(() => {
    window.addEventListener('resize', () => {
      setMobile(document.body.clientWidth < 768);
    });
    setMobile(document.body.clientWidth < 768);
  }, []);

  const pagination = {
    clickable: true,
    bulletClass: styles.bulletClass,
    bulletActiveClass: styles.bulletActiveClass,
    renderBullet: function (index: number, className: string) {
      return '<div class="' + className + '">' + '<div class="' + styles.progress + '"></div>' + '</div>';
    },
  };

  const { promotionBanner } = useSelector(
    (state: AppState) => ({
      promotionBanner: state.homePage.promotionBanner,
    }),
    shallowEqual,
  );

  return (
    <>
      <div className={classNames(styles.blog, 'Blog_Body')}>
        {body.map((item: any, index: number) => {
          if (item['carousel']) {
            const articles = item?.carousel?.carousel?.[0].articles?.slice(0, 5) || [];
            return (
              <div key={index} className={styles.header_swiper_block}>
                <div className={styles.header_block}>
                  <Header language={language} articleData={page} header={headerFooter} />
                </div>
                <h1 className={styles.carosel_title}>{item?.carousel?.carousel?.[0].carousel_title}</h1>
                <div className={styles.swiper_block}>
                  <Swiper
                    effect="creative"
                    pagination={pagination}
                    autoplay={{ delay: 7000, disableOnInteraction: false }}
                    modules={[Pagination, Autoplay]}
                  >
                    {articles?.length > 0 &&
                      articles.map((swiperItem: any, swiperIndex: number) => {
                        const articleItem = swiperItem?.article?.[0];
                        return (
                          <SwiperSlide key={swiperIndex}>
                            <div className={styles.swiper_item_container}>
                              <Image
                                src={
                                  isMobile
                                    ? swiperItem.image_mobile?.url || articleItem.blog_snippet?.banner_mobile?.url
                                    : swiperItem.image_desktop?.url || articleItem.blog_snippet?.banner_desktop?.url
                                }
                                className={styles.bg_Img}
                                alt={''}
                                fill
                              />
                              <div className={styles.swiper_item}>
                                <div className={styles.small_text_block}>
                                  <span className={styles.category_title}>
                                    {articleItem.blog_snippet?.category?.[0]?.title}
                                  </span>
                                  <div className={styles.date_wrapper}>
                                    <Image
                                      src="/blog/icon/CalendarWhite.svg"
                                      className={styles.calendar}
                                      alt={''}
                                      width={12}
                                      height={12}
                                    />
                                    {language.origin === 'zh' ? (
                                      <span className={styles.date}>
                                        {dayJs(articleItem?.blog_snippet?.last_update_date).format('YYYY年M月D日')}
                                      </span>
                                    ) : (
                                      <span className={styles.date}>
                                        {dayJs(articleItem?.blog_snippet?.last_update_date).format('D MMM YYYY')}
                                      </span>
                                    )}
                                  </div>
                                  <div className={styles.rate_wrapper}>
                                    <Image src="/blog/icon/StarWhite.svg" alt={''} width={13} height={12} />
                                    <span className={styles.rate}>
                                      {articleItem.blog_snippet?.blog_rating
                                        ? articleItem.blog_snippet?.blog_rating.toFixed(1)
                                        : '5.0'}
                                    </span>
                                  </div>
                                </div>
                                <h4 className={styles.swiper_item_title}>{articleItem.blog_snippet?.blog_title}</h4>
                                <Link
                                  target={swiperItem?.article_open_option === 'Self Window' ? '_self' : '_blank'}
                                  href={`/${
                                    language.origin
                                  }/blog/${articleItem.blog_snippet?.category?.[0]?.url.replace(/\/([^/]+)\//, '$1')}${
                                    articleItem?.url
                                  }`}
                                >
                                  <button className={styles.learn_more_button}>
                                    {language.origin === 'zh' ? '了解更多' : 'Know more'}
                                  </button>
                                </Link>
                              </div>
                            </div>
                          </SwiperSlide>
                        );
                      })}
                  </Swiper>
                </div>
              </div>
            );
          }
          if (item['search_bar']) {
            const searchBar = item.search_bar;
            const hotTopicList: any[] = searchBar.hot_topics || [];

            return (
              <XyzTransition key={index} appearVisible xyz=" delay-1 fade-100% down-2">
                <div
                  className={classNames(
                    styles.Search_Bar,
                    styles.MuiContainer_root,
                    styles.MuiContainer_maxWidthXl,
                    'Search_Bar',
                  )}
                >
                  <div className={styles.Search_Input_Wrap}>
                    <SearchInput
                      className={styles.Search_Input}
                      placeholder={searchBar.prefilled_text}
                      onClick={ev => goToSearch(ev)}
                    />
                  </div>

                  <div className={styles.Hot_Theme}>
                    {Array.isArray(hotTopicList) && <ScrollTag hotTopicList={hotTopicList.slice(0, 10)} />}
                  </div>
                </div>
              </XyzTransition>
            );
          }
          if (item['category_cards']) {
            const categoryCards = item.category_cards;
            return (
              <div
                key={index}
                className={classNames(
                  styles.MuiContainer_root,
                  styles.MuiContainer_maxWidthXl,
                  styles.Category_Card,
                  'Category_Card',
                )}
              >
                <XyzTransition appearVisible xyz=" delay-1 fade-100% down-2">
                  <div>
                    <CategoryWrapper key={index} data={categoryCards.categories} />
                  </div>
                </XyzTransition>
              </div>
            );
          }
          if (item['latest_blog_list']) {
            return (
              <div key={index} className={styles.Latest_Article}>
                <BlogSwiper homePage={page} isMultiple={true} />
              </div>
            );
          }
          if (item['hot_articles'] && language.origin !== 'en') {
            const editorChoices = item.hot_articles;
            const hotArticlesBlock =
              editorChoices.hot_articles && editorChoices.hot_articles?.length ? editorChoices.hot_articles[0] : {};

            return (
              <div key={index} className={styles.Verticle_Blog_List}>
                <div className={styles.Editor_Choice_Wrap}>
                  <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl)}>
                    <h3 className={styles.Title}>{hotArticlesBlock?.hot_article_title}</h3>
                    <div className={styles.Editor_Choice_List}>
                      {hotArticlesBlock?.articles
                        ?.slice(0, 6)
                        ?.map((item: any, k: any) =>
                          item?._metadata?.uid ? (
                            <GuideArticleBlock key={item.uid} className={styles.Editor_Choice} pageData={item} />
                          ) : (
                            ''
                          ),
                        )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          if (item['verticle_blog_list']) {
            const editorChoices = item.verticle_blog_list;
            return (
              <div key={index} className={styles.Verticle_Blog_List}>
                <div className={styles.Editor_Choice_Wrap}>
                  <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl)}>
                    <h3 className={styles.Title}>{editorChoices?.title}</h3>
                    <div className={styles.Editor_Choice_List}>
                      {editorChoices?.blog_post
                        ?.slice(0, 6)
                        ?.map((item: any, k: any) =>
                          item.created_at ? (
                            <EditorChoice key={item.uid} className={styles.Editor_Choice} pageData={item} />
                          ) : (
                            ''
                          ),
                        )}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          if (
            item['banner'] &&
            item['banner']['banner_reference'][0] &&
            item['banner']['banner_reference'][0]['_content_type_uid'] === ECOM_PROMOTIONS
          ) {
            const bannerReference = item.banner.banner_reference || [];
            const promotionsList = bannerReference[0].promotion || [];
            const promotions =
              Array.isArray(promotionsList) && promotionsList.length > 0
                ? promotionsList.map((item: any) => item.promotion)
                : [];
            return (
              <div key={index} className={classNames(styles.Promotion_Card_Wrap, 'Follow_Background_Color')}>
                <XyzTransition appearVisible xyz=" delay-1 fade-100% down-2">
                  <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl)}>
                    <div className={styles.Promotion_Card}>
                      {promotions.map((item: any, k: any) => {
                        return (
                          <PromotionCard
                            key={k}
                            promotion={item}
                            language={language.origin}
                            className={styles.Promotion_Card_Item}
                          />
                        );
                      })}
                    </div>
                  </div>
                </XyzTransition>
              </div>
            );
          }
          if (
            item['banner'] &&
            item['banner']['banner_reference'][0] &&
            item['banner']['banner_reference'][0]['_content_type_uid'] === SHARED_BANNER_MODAL
          ) {
            const bannerReference = item.banner.banner_reference[0] || {};
            const promotionType =
              bannerReference && bannerReference.type_of_promotion && bannerReference.type_of_promotion[0]
                ? bannerReference.type_of_promotion[0]
                : {};

            let promotionBannerData;
            if (Object.keys(promotionType).includes('banner')) {
              promotionBannerData = bannerReference;
            }

            return (
              <div key={index} className={'Follow_Background_Color'}>
                <PromotionBanner bannerData={promotionBannerData} className={styles.Promotion_Banner} />
              </div>
            );
          }
        })}
        {Array.isArray(promotionBanner) && promotionBanner.length > 0 ? (
          <div className={styles.Bottom_Promotion_Banner}>
            {promotionBanner.map((item: any, index: number) => {
              const TypePromotion = item.type_of_promotion || [];
              const promotionType = TypePromotion[0] || {};

              if (Object.keys(promotionType).includes('banner')) {
                return <PromotionBanner key={index} bannerData={item} className={'Home_Banner'} />;
              }
            })}
          </div>
        ) : (
          ''
        )}
      </div>
    </>
  );
});

export const getServerSideProps = wrapper.getServerSideProps(store => async (ctx: ServerContext) => {
  try {
    const startTime = new Date().getTime();
    info('blog index ctx: ', ctx);
    // Extract language from URL path
    let language = (ctx.query.language as string) ?? null;
    if (!language && ctx.req?.url) {
      const match = ctx.req.url.match(/^\/(zh|en)\/blog\/?/);
      if (match) {
        language = match[1];
      }
    }

    const lang = language === 'en' ? 'en-us' : 'zh-hk';
    const pathname = '/blog';
    const isSvg = filterSvgPath(ctx.req?.url);
    let promotionBannerData;
    let popUpBannerData;

    if (!isSvg) {
      const [result1, result2, result3, result4, result5, result6]: any[] = await Promise.all([
        getAllCategory(lang),
        getAllCategoryLanding(lang),
        getPromotionBanner(pathname, lang),
        getMultiplePopupBanner(pathname, lang),
        getHeaderAndFooter(HEADER_FOOTER_CONTENT_TYPE, headerAndFooterIncludeReference, lang),
        getEntryByContentTypeUrl(
          HOME_PAGE_CONTENT_TYPE,
          HOME_PAGE_URL,
          [...homePageIncludeReference, ...otherHomePageReference],
          lang,
        ),
      ]);
      changeDefaultData(store, result1, result2, result3, result4, result5);

      promotionBannerData = result3?.data;
      popUpBannerData =
        Array.isArray(result4?.data) && Array.isArray(result4?.data[0]) && result4?.data[0].length > 0
          ? result4?.data[0][0]
          : {};

      const homePage = Array.isArray(result6?.data) && Array.isArray(result6?.data[0]) ? result6?.data[0][0] : {};
      store.dispatch(changeHomePage(homePage));
      store.dispatch(getHomePageSuccess(homePage.body || []));
    }

    const { homePage } = store.getState();
    const ip = ctx.req.headers['x-forwarded-for'] || ctx.req.socket.remoteAddress || '';

    const props = {
      page: homePage.homePage || {},
      body: homePage.body || [],
      query: ctx.query || {},
      pathName: ctx.resolvedUrl || '',
      ip: ip || '',
      startTime: startTime,
      promotionBannerData: promotionBannerData || null,
      popUpBannerData: popUpBannerData || null,
      homepage: homePage,
    };
    return {
      props: props,
    };
  } catch (e) {
    error('blog/index SSR fetch error:', e);
    return { notFound: true };
  }
});

export const changeDefaultData = (store: any, result1: any, result2: any, result3: any, result4: any, result5: any) => {
  store.dispatch(changeAllCategory(Array.isArray(result1?.data) ? result1?.data[0] || [] : []));
  store.dispatch(changeAllCategoryLanding(Array.isArray(result2?.data) ? result2?.data[0] || [] : []));
  store.dispatch(changePromotionBanner(Array.isArray(result3?.data) ? result3?.data[0] : null));

  store.dispatch(
    changePopupBanner(Array.isArray(result4?.data) && Array.isArray(result4?.data[0]) ? result4.data[0][0] : null),
  );

  const headerFooter: any = Array.isArray(result5?.data) && Array.isArray(result5?.data[0]) ? result5?.data[0][0] : {};
  const categoryList =
    Array.isArray(headerFooter?.blog_menu?.menu_item) &&
    headerFooter.blog_menu.menu_item.map((category: any) => ({ ...category, active: false }));

  store.dispatch(changeHeaderFooter(headerFooter || null));
  store.dispatch(changeCategoryList(categoryList || []));
};
